import React, {Component} from 'react';
import moment from 'moment';

import classNames from 'classnames';
import strings from 'utils/strings';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPrint} from '@fortawesome/free-solid-svg-icons';

const childTypeGrades = [
	'child-screening',
	'child',
	'youth-screening',
	'youth-followup',
];

const NOT_AVAILABLE = 'Inte tillgänglig';

/**
 * Safe getter for nested values.
 * Example:
 * get(testUser, 'education.youthGrade')
 */
const get = (object, path, fallback) => {
	if (!object || !path) {
		return fallback;
	}

	const value = path.split('.').reduce((current, key) => {
		if (current && Object.prototype.hasOwnProperty.call(current, key)) {
			return current[key];
		}

		return undefined;
	}, object);

	return value === undefined || value === null ? fallback : value;
};

const hasValue = (value) => {
	return value !== undefined && value !== null && value !== '';
};

const getContent = (props) => {
	if (hasValue(props.children)) {
		return props.children;
	}

	if (hasValue(props.value)) {
		return props.value;
	}

	return NOT_AVAILABLE;
};

const formatBool = (value, hasLongIntro) => {
	if (
		value === 'yes' ||
		value === 'ja' ||
		value === true ||
		value === 'true'
	) {
		return 'Ja';
	}

	if (
		value === 'no' ||
		value === 'nei' ||
		value === false ||
		value === 'false'
	) {
		return 'Nei';
	}

	if (value === '') {
		return 'ikke tilgjengelig';
	}

	if (hasLongIntro && typeof value === 'undefined') {
		return 'ikke tilgjengelig';
	}

	if (hasLongIntro && value) {
		return 'Ja';
	}

	if (hasLongIntro && !value) {
		return 'Nei';
	}

	if (!hasLongIntro && !value) {
		return 'Nei';
	}

	return '';
};

const formatAge = (age) => {
	const years = get(age, 'years');
	const months = get(age, 'months');

	if (!hasValue(years) && !hasValue(months)) {
		return NOT_AVAILABLE;
	}

	if (hasValue(years) && hasValue(months)) {
		return `${years} år och ${months} månader`;
	}

	if (hasValue(years)) {
		return `${years} år`;
	}

	return `${months} månader`;
};

const formatGender = (gender, testType) => {
	const isChildType = childTypeGrades.includes(testType);

	if (gender === 'm' && isChildType) {
		return 'Pojke';
	}

	if (gender === 'f' && isChildType) {
		return 'Flicka';
	}

	if (gender === 'm' && !isChildType) {
		return 'Man';
	}

	if (gender === 'f' && !isChildType) {
		return 'Kvinna';
	}

	return NOT_AVAILABLE;
};

const formatDate = (date) => {
	if (!date) {
		return NOT_AVAILABLE;
	}

	const parsedDate = moment(date);

	if (!parsedDate.isValid()) {
		return NOT_AVAILABLE;
	}

	return parsedDate.format('LL');
};

const InfoRow = (props) => {
	if (props.condition !== undefined && !props.condition) {
		return null;
	}

	return (
		<p className="info__row">
			<span className="info__label">{props.name}</span>
			<span className="info__value">{getContent(props)}</span>
		</p>
	);
};

const InfoRowBool = (props) => {
	const value = hasValue(props.children) ? props.children : props.value;
	const valueText = formatBool(value, props.hasLongIntro);

	return (
		<InfoRow condition={props.condition} name={props.name}>
			{valueText || NOT_AVAILABLE}
		</InfoRow>
	);
};

class TestUserInfo extends Component {
	render() {
		const testResult = this.props.testResult || {};

		/*
			Normally the data is expected as:
			testResult.testUser

			This fallback also allows the component to survive if it receives
			a partial user object directly, for example:
			{ education: { youthGrade: 6 } }
		*/
		const testUser = testResult.testUser || testResult || {};

		const testType = this.props.testType;
		const hasLongIntro = !!testResult.anamnese;
		const isTestOwner = !!testResult.isTestOwner;

		const firstName = get(testUser, 'firstName', '');
		const lastName = get(testUser, 'lastName', '');
		const email = get(testUser, 'email', '');
		const phone = get(testUser, 'phone', '');

		const completeName = firstName
			? `${firstName} ${lastName || ''}`.trim()
			: strings.hiddenname;

		const completeEmail = firstName && email ? email : 'Inte tillgänglig';

		const youthGrade = get(testUser, 'education.youthGrade');
		const isInHighSchool = get(testUser, 'education.isInHighSchool');

		const age = get(testUser, 'age', {});
		const gender = get(testUser, 'gender');

		const completedAt = get(testResult, 'completedAt');
		const hasCompletedAt = completedAt && moment(completedAt).isValid();

		return (
			<div>
				<div className="lit-input-information">
					<button
						className={classNames('print-hide', {
							'lit-btn2 see-more': true,
							'bg-tertiary': true,
						})}
						onClick={() => {
							window.print();
							/* this.updatePrintLog('Results'); */
						}}
					>
						<FontAwesomeIcon
							size="lg"
							className="lit-icon-btns"
							icon={faPrint}
						/>

						<p className="lit-btns-txt">
							{'Skriv ut kandidatinformation'}
						</p>
					</button>
				</div>

				<div className="info">
					<InfoRow
						condition={isTestOwner}
						name={`Namn:`}
						hasLongIntro={hasLongIntro}
					>
						{completeName}
					</InfoRow>

					{testType === 'child-screening' && (
						<InfoRow
							condition={
								hasValue(youthGrade) || isInHighSchool === true
							}
							hasLongIntro={hasLongIntro}
							name={`Årskurs: `}
						>
							{hasValue(youthGrade) ? youthGrade : NOT_AVAILABLE}
						</InfoRow>
					)}

					<InfoRow
						condition={hasCompletedAt}
						name="Datum för testgenomförandet: "
						value={formatDate(completedAt)}
					/>

					<InfoRow
						condition={isTestOwner}
						name="E-post:"
						value={completeEmail}
						hasLongIntro={hasLongIntro}
					/>

					<InfoRow
						condition={isTestOwner}
						name="Telefon:"
						value={phone || NOT_AVAILABLE}
						hasLongIntro={hasLongIntro}
					/>

					<InfoRow
						name="Ålder vid testgenomförandet:"
						value={formatAge(age)}
						hasLongIntro={hasLongIntro}
					/>

					<InfoRow
						name="Kön:"
						value={formatGender(gender, testType)}
						hasLongIntro={hasLongIntro}
					/>
				</div>
			</div>
		);
	}
}

export default TestUserInfo;
